/**
 * usePackageAdvanceCollection
 *
 * Encapsulates the complete 3-step package-advance-collection flow:
 *
 *  1. InsufficientAdvancePopup   → prompts user to collect more advance
 *  2. CollectPackageAdvanceModal → select package + enter amount  (Step 1)
 *  3. CollectPaymentModal        → choose payment method            (Step 2)
 *  4. API submission              → POST /payments/                  (Step 3)
 *
 * The hook is fully self-contained: it owns the modal states, fetches
 * packages, and submits the advance.  The consumer only needs to render
 * the modals and wire the actions returned from this hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postRequest } from '../../../../services/httpService';
import {
  postMakeAdvancePaymentURL,
  type MakePayment,
  type PaymentDetailPayload,
} from '../../../../pages/Billing/Advance/advance.helper';
import {
  fetchActivePackages,
  useValidatePackageAdvance,
  buildValidatePackageAdvanceParams,
  type ActivePackageItem,
  type ValidatePackageAdvanceResponse,
} from '../../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';
import { formatDateDMY, getTodayYMD, toISODateString } from '../../../../utils/dateUtils';
import { useCurrency } from '../../../CurrencyProvider';
import {
  shouldValidatePackageAdvance,
  MODAL_TRANSITION_DELAY_MS,
  POST_ADVANCE_SETTLE_DELAY_MS,
  DEFAULT_ADVANCE_AGAINST,
  ADVANCE_TYPE,
  ADVANCE_PAYMENT_AGAINST_TYPE,
} from './config';
import {
  buildPaymentDetails,
  computeConsumeAmount,
  buildPackageAmountMap,
  extractPackageNames,
  sumPackageBalance,
} from './utils';
import type {
  PackageAdvanceCollectionState,
  PackageAdvanceCollectionActions,
  UsePackageAdvanceCollectionProps,
  UsePackageAdvanceCollectionReturn,
  PackageOption,
  PackageAdvanceFormData,
  InvoiceRowData,
  PaymentFormValues,
  PaymentHistoryItem,
  PaymentDetails,
} from './types';

// ============================================================================
// Helpers (pure)
// ============================================================================

/** Map API package item → local PackageOption shape */
const mapPackageToOption = (pkg: ActivePackageItem): PackageOption => ({
  id: pkg.id,
  name: pkg.package_name || pkg.name,
  startDate: pkg.start_date ?? undefined,
  packageAmount: pkg.package_amount,
  packageNetAmount: pkg.package_net_amount,
  packageConcessionAmount: pkg.package_concession_amount,
  totalServiceConcessionAmount: pkg.total_service_concession_amount,
  packageAdvanceCollected: pkg.package_advance_collected,
  advanceCollected: pkg.advance_collected,
  packageUsedAmount: pkg.package_used_amount,
  packageBalanceAmount: pkg.package_balance_amount,
  serviceRemainingAmount: pkg.service_remaining_amount,
  pharmacyRemainingAmount: pkg.pharmacy_remaining_amount,
  serviceAmount: pkg.service_amount,
  pharmacyAmount: pkg.pharmacy_amount,
  pharmacyComponentAmount: pkg.pharmacy_component_amount,
  pharmacyComponentPercent: pkg.pharmacy_component_percent,
  collectedPharmacyAdvanceAmount: pkg.collected_pharmacy_advance_amount,
  remainingPharmacyAdvanceAmount: pkg.remaining_pharmacy_advance_amount,
});

// ============================================================================
// Initial state
// ============================================================================

const INITIAL_STATE: PackageAdvanceCollectionState = {
  collectPackageAdvanceOpen: false,
  collectPackagePaymentOpen: false,
  insufficientAdvanceOpen: false,
  packageOptions: [],
  packageAdvanceFormData: null,
  packageAdvancePaymentDetails: null,
  settleRowForPackage: null,
  insufficientAdvanceTitle: '',
  insufficientAdvanceMessage: '',
  insufficientAdvanceButtonText: '',
  isValidating: false,
  isAdvanceSubmitting: false,
};

// ============================================================================
// Hook
// ============================================================================

export function usePackageAdvanceCollection(
  props: UsePackageAdvanceCollectionProps,
): UsePackageAdvanceCollectionReturn {
  const {
    patientId,
    clinicId,
    currency,
    onOpenRegularPayment,
    onAlert,
    onAdvancePaymentSuccess,
    onSettleAfterAdvance,
  } = props;

  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const modalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup modal transition timer on unmount to prevent stale setState calls
  useEffect(() => {
    return () => {
      if (modalTimerRef.current) {
        clearTimeout(modalTimerRef.current);
        modalTimerRef.current = null;
      }
    };
  }, []);

  // ---- Local state ----
  const [state, setState] = useState<PackageAdvanceCollectionState>(INITIAL_STATE);

  // Convenience setter
  const patch = useCallback(
    (partial: Partial<PackageAdvanceCollectionState>) =>
      setState((prev) => ({ ...prev, ...partial })),
    [],
  );

  // ---- Mutation: validate package advance ----
  const validatePackageAdvanceMutation = useValidatePackageAdvance();

  // ---- Mutation: save advance payment ----
  const { mutate: submitAdvancePayment } = useMutation({
    mutationFn: (payload: MakePayment) =>
      postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      // Capture the row and package name before clearing state —
      // we need them to auto-open the settle modal after advance success.
      const savedRow = state.settleRowForPackage;
      const savedPackageName = state.packageAdvanceFormData?.packageName;

      onAlert(tBilling('invoice.package_advance.advance_collected_success'), 'success');
      patch({
        collectPackagePaymentOpen: false,
        packageAdvanceFormData: null,
        packageAdvancePaymentDetails: null,
        isAdvanceSubmitting: false,
        settleRowForPackage: null,
      });

      // Invalidate relevant queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onAdvancePaymentSuccess?.();

      // Auto-open the regular settle payment modal for the same invoice
      // so the user doesn't have to click "Settle Bill" again.
      if (savedRow && onSettleAfterAdvance) {
        const details = buildPaymentDetails(savedRow, currency || 'INR');
        const consumeAmt = computeConsumeAmount(savedRow.advance_consumption_metadata);

        // Small delay so the advance modal fully closes before settle opens
        if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
        modalTimerRef.current = setTimeout(() => {
          onSettleAfterAdvance(
            savedRow,
            details,
            consumeAmt > 0 ? consumeAmt : undefined,
            undefined,
            savedPackageName || undefined,
          );
          modalTimerRef.current = null;
        }, POST_ADVANCE_SETTLE_DELAY_MS);
      }
    },
    onError: (err: unknown) => {
      const message =
        (err as { message?: string })?.message ||
        tBilling('invoice.package_advance.advance_save_failed');
      onAlert(message, 'error');
      patch({ isAdvanceSubmitting: false });
    },
  });

  // ===========================================================================
  // Fetch active packages for the patient
  // ===========================================================================
  const fetchAndMapPackages = useCallback(async (): Promise<PackageOption[]> => {
    if (!patientId) return [];
    try {
      const packages = await fetchActivePackages(patientId);
      return (packages || []).map(mapPackageToOption);
    } catch {
      return [];
    }
  }, [patientId]);

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Main settle handler.
   *
   * Flow:
   *  1. Check if the row has advance_consumption_metadata (package-linked)
   *  2. If yes → build packages from metadata → call validate-package-advance API
   *  3. If overall_valid === true  → open regular settle (advance is sufficient)
   *  4. If overall_valid === false → show InsufficientAdvancePopup
   *  5. If no metadata → return false so caller opens regular settle
   *
   * Returns `true` when it has handled the flow.
   * Returns `false` when the caller should open the regular settle modal.
   */
  const handleSettleBillWithPackageCheck = useCallback(
    async (row: InvoiceRowData): Promise<boolean> => {
      const hasMetadata =
        Array.isArray(row.advance_consumption_metadata) &&
        row.advance_consumption_metadata.length > 0;

      if (!shouldValidatePackageAdvance(row.billType, hasMetadata)) {
        return false; // → caller opens regular settle
      }

      patch({ isValidating: true, settleRowForPackage: row });

      // ---- Step 1: Build per-package required amounts from metadata ----
      const metadata = row.advance_consumption_metadata!;
      const pkgAmountMap = buildPackageAmountMap(metadata);
      const packagesForValidation = Array.from(pkgAmountMap.entries()).map(
        ([package_id, amount]) => ({ package_id, amount }),
      );
      // Total consume amount shown on the banner
      const totalConsumedFromPackage = computeConsumeAmount(metadata);

      // ---- Step 2: Call validate-package-advance API ----
      let validationResult: ValidatePackageAdvanceResponse | null = null;
      try {
        validationResult = await validatePackageAdvanceMutation.mutateAsync(
          buildValidatePackageAdvanceParams(packagesForValidation),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Package advance validation failed:', err);
        // On API error, fall back to regular settle
        patch({ isValidating: false });
        return false;
      }

      // ---- Step 3: Branch on result ----
      if (validationResult?.overall_valid === true) {
        // Advance is sufficient → open regular settle modal directly
        patch({ isValidating: false });

        const totalPackageBalance = sumPackageBalance(validationResult.packages);
        const packageNameStr = extractPackageNames(validationResult.packages);

        onOpenRegularPayment(
          buildPaymentDetails(row, currency),
          totalConsumedFromPackage,
          totalPackageBalance,
          packageNameStr,
          row.advance_consumption_metadata ?? undefined,
        );
        return true;
      }

      // ---- Step 4: Insufficient advance → fetch packages, show popup ----
      const packageOptions = await fetchAndMapPackages();

      if (packageOptions.length === 0) {
        // No active packages – fall back to regular settle
        patch({ isValidating: false });
        onOpenRegularPayment(buildPaymentDetails(row, currency));
        return true;
      }

      // Build a descriptive message from the validation response
      const insufficientPackages = validationResult?.packages?.filter((p) => !p.valid) || [];
      // Build a lookup map so we can show the package name instead of the ID
      const pkgNameById = new Map(packageOptions.map((o) => [o.id, o.name]));
      const messageParts = insufficientPackages.map((p) => {
        const name = pkgNameById.get(p.package_id) || `Package #${p.package_id}`;
        return tBilling('invoice.package_advance.insufficient_detail', {
          name,
          required: formatCurrency(p.required_amount ?? 0),
          available: formatCurrency(p.balance_amount ?? 0),
        });
      });
      const detailMessage =
        messageParts.length > 0
          ? messageParts.join('\n\n')
          : (validationResult?.message ||
            tBilling('invoice.package_advance.default_insufficient_message'));

      patch({
        isValidating: false,
        packageOptions,
        insufficientAdvanceOpen: true,
        insufficientAdvanceTitle: tBilling('invoice.package_advance.insufficient_title'),
        insufficientAdvanceMessage: detailMessage,
        insufficientAdvanceButtonText: tBilling('invoice.package_advance.collect_advance'),
      });
      return true;
    },
    [fetchAndMapPackages, currency, formatCurrency, onOpenRegularPayment, patch, tBilling, validatePackageAdvanceMutation],
  );

  /**
   * Opens the package advance flow directly from the menu item.
   */
  const handleCollectAdvanceFromMenu = useCallback(
    async (row: InvoiceRowData) => {
      patch({ isValidating: true, settleRowForPackage: row });
      const packageOptions = await fetchAndMapPackages();

      if (packageOptions.length === 0) {
        patch({ isValidating: false });
        onAlert(tBilling('invoice.package_advance.no_active_packages_for_patient'), 'warning');
        return;
      }

      // Pre-select the package from metadata if available
      patch({
        isValidating: false,
        packageOptions,
        collectPackageAdvanceOpen: true,
      });
    },
    [fetchAndMapPackages, onAlert, patch, tBilling],
  );

  /**
   * Transition from insufficient popup → Step 1 modal.
   */
  const handleOpenCollectPackageAdvance = useCallback(async () => {
    patch({ insufficientAdvanceOpen: false });

    // Re-fetch in case data changed
    const packageOptions = await fetchAndMapPackages();
    if (packageOptions.length === 0) {
      onAlert(tBilling('invoice.package_advance.no_active_packages'), 'warning');
      return;
    }

    patch({ packageOptions, collectPackageAdvanceOpen: true });
  }, [fetchAndMapPackages, onAlert, patch, tBilling]);

  /**
   * Step 1 → Step 2: receive form data, prepare payment details, open
   * the payment-methods modal.
   */
  const handlePackageAdvanceCollectPayment = useCallback(
    (formData: PackageAdvanceFormData) => {
      if (!formData || formData.advanceAmount <= 0) {
        onAlert(tBilling('invoice.package_advance.enter_valid_advance_amount'), 'warning');
        return;
      }

      // Use functional update to read settleRowForPackage from latest state
      // (avoids stale-closure issue when the callback was created before the
      //  row was stored via patch).
      setState((prev) => {
        const row = prev.settleRowForPackage;

        const paymentDetails: PaymentDetails = {
          date: formatDateDMY(),
          patientName: row?.patientName || tBilling('invoice.package_advance.patient_fallback'),
          patientId: String(
            patientId ?? row?.patientId ?? row?.patient_id ?? '',
          ),
          currency: currency || 'INR',
          netAmount: formData.advanceAmount,
        };

        return {
          ...prev,
          packageAdvanceFormData: formData,
          collectPackageAdvanceOpen: false,
          packageAdvancePaymentDetails: paymentDetails,
        };
      });

      // Small delay for smooth modal transition
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
      modalTimerRef.current = setTimeout(() => {
        patch({ collectPackagePaymentOpen: true });
        modalTimerRef.current = null;
      }, MODAL_TRANSITION_DELAY_MS);
    },
    [patientId, currency, onAlert, patch, tBilling],
  );

  /**
   * Step 2 → Step 3: build payload & submit advance payment.
   */
  const handleSavePackageAdvance = useCallback(
    async (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
      const formData = state.packageAdvanceFormData;
      if (!formData) {
        onAlert(tBilling('invoice.package_advance.data_not_found'), 'error');
        patch({ collectPackagePaymentOpen: false });
        return;
      }
      if (!history?.length) {
        onAlert(tBilling('invoice.package_advance.add_payment_method'), 'warning');
        return;
      }

      const totalPaymentAmount = history.reduce(
        (sum, item) => sum + (item.amount || 0),
        0,
      );

      if (Math.abs(totalPaymentAmount - formData.advanceAmount) > 0.01) {
        onAlert(
          tBilling('invoice.package_advance.payment_amount_mismatch', {
            paymentAmount: totalPaymentAmount,
            advanceAmount: formData.advanceAmount,
          }),
          'warning',
        );
        return;
      }

      if (!patientId || !clinicId) {
        onAlert(tBilling('invoice.package_advance.missing_patient_or_clinic'), 'error');
        return;
      }

      patch({ isAdvanceSubmitting: true });

      const balanceAmount = Math.max(
        (formData.packageAmount ?? 0) -
          (formData.packageAdvanceCollected ?? 0),
        0,
      );

      const paymentCurrency = currency || 'INR';

      const payment_details: PaymentDetailPayload[] = history.map((item) => {
        const base: PaymentDetailPayload = {
          payment_mode_id: item.payment_mode_id ?? 0,
          paid_amount: String(item.amount ?? 0),
          currency: paymentCurrency,
          number: null,
          date: toISODateString(item.date),
          bank_id: null,
          account_name: null,
          branch_name: null,
          ifsc_code: null,
          remarks: item.notes ?? formData.remark ?? null,
          cheque_number: null,
          cheque_date: null,
        };

        if (
          item.method?.toLowerCase().includes('bank') ||
          item.method?.toLowerCase().includes('online') ||
          item.method?.toLowerCase().includes('card') ||
          item.method?.toLowerCase().includes('upi')
        ) {
          base.number =
            item.transactionNumber ?? item.cardTransactionNumber ?? null;
        }

        if (item.method?.toLowerCase().includes('cheque')) {
          base.cheque_number = item.chequeNumber ?? null;
          base.cheque_date = item.chequeDate
            ? toISODateString(item.chequeDate)
            : null;
        }

        return base;
      });

      const row = state.settleRowForPackage;

      const payload: MakePayment = {
        invoice_id: null,
        patient_id: Number(patientId),
        clinic_id: Number(clinicId),
        payment_against_type: ADVANCE_PAYMENT_AGAINST_TYPE,
        advance_data: {
          advance_against: formData.advanceAgainst || DEFAULT_ADVANCE_AGAINST,
          advance_type: ADVANCE_TYPE,
          date: formData.advanceDate || getTodayYMD(),
          amount: totalPaymentAmount,
          remark: formData.remark || '',
          appointment_visit: row?.appointment_id ?? 0,
          patient_package_id: formData.selectedPackageId,
          balance_amount: balanceAmount,
        },
        remarks: formData.remark || '',
        payment_details,
      };

      submitAdvancePayment(payload);
    },
    [
      state.packageAdvanceFormData,
      state.settleRowForPackage,
      patientId,
      clinicId,
      currency,
      onAlert,
      patch,
      submitAdvancePayment,
      tBilling,
    ],
  );

  // ===========================================================================
  // Close / Reset helpers
  // ===========================================================================

  const closeInsufficientAdvancePopup = useCallback(
    () => patch({ insufficientAdvanceOpen: false }),
    [patch],
  );

  const closeCollectPackageAdvanceModal = useCallback(
    () => patch({ collectPackageAdvanceOpen: false }),
    [patch],
  );

  const closeCollectPackagePaymentModal = useCallback(
    () =>
      patch({
        collectPackagePaymentOpen: false,
        packageAdvancePaymentDetails: null,
      }),
    [patch],
  );

  const resetPackageAdvanceState = useCallback(
    () => setState(INITIAL_STATE),
    [],
  );

  // ===========================================================================
  // Return
  // ===========================================================================

  const actions: PackageAdvanceCollectionActions = {
    handleSettleBillWithPackageCheck,
    handleCollectAdvanceFromMenu,
    handleOpenCollectPackageAdvance,
    handlePackageAdvanceCollectPayment,
    handleSavePackageAdvance,
    closeInsufficientAdvancePopup,
    closeCollectPackageAdvanceModal,
    closeCollectPackagePaymentModal,
    resetPackageAdvanceState,
  };

  return { state, actions };
}

export default usePackageAdvanceCollection;
